import { precedentConsensus, precedentTitleConsensus } from "./precedentConsensus";
import { historyFor, leadHistory } from "./historySignalStore";

/**
 * 출처별 세번(2026-09-28) — 관세율표 명칭·국내 결정례·관세사 신고이력이 각각 가리키는 세번을 나란히 둔다.
 * 새로 추측하지 않는다: 카드가 이미 받은 데이터만 읽는다. 같은 세번(또는 같은 소호·호)을 가리키면 묶어 보이고,
 * 다르면 사용자가 줄을 눌러 지도에서 확인한다. 판정은 트리가 한다(표시·길 안내일 뿐).
 *
 * 반환 { rows: [{ kind, code, level, ... }], agreement: { code, level, kinds } | null }
 *  - level: 10(세번)·6(소호)·4(호)
 *  - agreement: 두 출처 이상이 같은 자리를 가리키는 가장 깊은 자리(10 → 6 → 4)
 */
export function sourceCodes({ tariffMatches = [], headingMatches = [], precedentHints = null, brokerHistory = null } = {}) {
  const rows = [];

  // 관세율표 — 10자리 품명 일치가 먼저, 없으면 호·소호 명칭 일치.
  const leaf = (Array.isArray(tariffMatches) ? tariffMatches : []).find((m) => digits(m?.hsk).length === 10);
  if (leaf) {
    const others = new Set((tariffMatches || []).map((m) => digits(m?.hsk)).filter((h) => h.length === 10 && h !== digits(leaf.hsk)));
    rows.push({ kind: "tariff", code: digits(leaf.hsk), level: 10, name: leaf.ko || leaf.en || "",
      via: leaf.nameVia === "llm" || leaf.llm ? "llm" : leaf.modifier ? "modifier" : "name", more: others.size });
  } else {
    const heads = (Array.isArray(headingMatches) ? headingMatches : [])
      .map((h) => ({ code: digits(h?.code), text: String(h?.line_ko || h?.line_en || "").replace(/^[\d.\s]+/, "").trim() }))
      .filter((h) => h.code.length === 4 || h.code.length === 6);
    if (heads.length) {
      const first = heads.slice().sort((a, b) => a.code.length - b.code.length)[0];
      rows.push({ kind: "tariff", code: first.code, level: first.code.length, name: first.text, via: "heading",
        also: heads.filter((h) => h !== first).map((h) => h.code) });
    }
  }

  // 국내 결정례 — 합의(3건·80%↑) 또는 제목 일치, 없으면 건수가 가장 많은 세번.
  const consensus = precedentConsensus(precedentHints, []) || precedentTitleConsensus(precedentHints);
  const byHsk = Array.isArray(precedentHints?.by_hsk) ? precedentHints.by_hsk.filter((r) => digits(r?.hsk).length === 10 && Number(r?.count) > 0) : [];
  const topRow = consensus ? byHsk.find((r) => digits(r.hsk) === consensus.hsk) || null
    : byHsk.slice().sort((a, b) => Number(b.count) - Number(a.count))[0] || null;
  if (consensus || topRow) {
    const code = consensus ? consensus.hsk : digits(topRow.hsk);
    const total = Number(precedentHints?.eligible_total ?? byHsk.reduce((s, r) => s + Number(r.count), 0)) || 0;
    const cnt = Number(topRow?.count || consensus?.count || 0);
    // 약한 근거(2026-09-29): 합의·제목 일치가 아니고 '최다'인데 절반도 안 되면 일치 판단에 넣지 않는다(제2165호 실측 — 13·37번이 잡음끼리 일치).
    const weak = !consensus && !(total > 0 && cnt / total >= 0.5);
    // 호 자리에서는 그 호 전체의 결정례 비율로 본다(같은 호의 여러 세번으로 나뉘어도 호는 모일 수 있다).
    const hs4Count = byHsk.filter((r) => digits(r.hsk).startsWith(code.slice(0, 4))).reduce((s2, r) => s2 + Number(r.count), 0);
    const weakHeading = !consensus && !(total > 0 && hs4Count / total >= 0.5);
    rows.push({ kind: "precedent", code, level: 10, count: cnt, total, weak, weakHeading,
      year: String(topRow?.latest_date || consensus?.latest_date || "").slice(0, 4),
      basis: consensus ? (consensus.title ? "title" : "consensus") : "most",
      label: String(topRow?.label || ""), excerpt: excerpt(topRow?.desc) });
  }

  // 관세사 신고이력 — 1위 세번(표본 비율)과 그 호의 합계.
  const top = brokerHistory?.top;
  if (top && digits(top.hsk).length === 10) {
    const code = digits(top.hsk);
    const heading = historyFor(brokerHistory, code.slice(0, 4));
    // 관세사 이력은 강한·앞선 신호(과반·2위의 두 배·같은 물품 근거 10건+)일 때만 일치 판단에 넣는다.
    // 호 자리에서는 그 호의 이력 합계가 과반이고 10건 이상이면 강한 것으로 본다(Shawls: 6214.20 41% 이지만 6214 호 83%).
    const weakHeading = !(heading && heading.share >= 50 && heading.observations >= 10) || Boolean(brokerHistory.broad);
    rows.push({ kind: "history", code, level: 10, weak: !leadHistory(brokerHistory), weakHeading, share: Number(top.share) || 0, observations: Number(top.observations) || 0,
      headingShare: heading ? heading.share : null, headingObservations: heading ? heading.observations : null,
      sampled: Boolean(brokerHistory.sampled), partial: Boolean(brokerHistory.partial), broad: Boolean(brokerHistory.broad) });
  }

  // PROVE 학습 모델(2026-09-29) — 결정례 정답지·관세사 신고이력으로 학습한 모델의 세번 제안(결정례 300건 10자리 81%·봉인 전 측정).
  // 다른 출처와 같은 무게로 일치 판단에 넣는다. 모델 이름은 보이지 않는다.
  const mp = precedentHints?.model_proposal;
  if (mp && digits(mp.hsk).length === 10) {
    rows.push({ kind: "model", code: digits(mp.hsk), level: 10 });
  }

  return { rows, agreement: agreementOf(rows) };
}

/** 두 출처 이상이 같은 자리를 가리키는 가장 깊은 자리. 같은 깊이에 여럿이면 출처가 많은 쪽. */
export function agreementOf(rows) {
  for (const level of [10, 6, 4]) {
    const groups = new Map();
    for (const r of rows || []) {
      if (!r || String(r.code).length < level) continue;
      if (level === 4 ? (r.weakHeading ?? r.weak) : r.weak) continue;   // 약한 근거는 그 자리의 일치 판단에 넣지 않는다
      const key = String(r.code).slice(0, level);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r.kind);
    }
    const best = [...groups.entries()].filter(([, kinds]) => new Set(kinds).size >= 2).sort((a, b) => b[1].length - a[1].length)[0];
    if (best) return { code: best[0], level, kinds: [...new Set(best[1])] };
  }
  return null;
}

export const SOURCE_LABEL = { tariff: "관세율표", precedent: "결정례", history: "관세사 이력", model: "PROVE 모델" };

function digits(v) { return String(v || "").replace(/\D/g, ""); }

/** 결정례 성상 원문의 앞부분 한 구절(표시용). */
function excerpt(desc) {
  // 원문의 가운뎃점이 '?'로 깨져 적재된 것(성상?규격)을 되돌린다.
  const s = String(desc || "").replace(/(?<=[가-힣])\?(?=[가-힣])/gu, "·")
    .replace(/[ㅇ○•·]\s*성상[^\s]*\s*/u, "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  const cut = s.split(/\s*[ㅇ○•]\s*원산지|\s*ㅇ\s/u)[0];
  return cut.length > 70 ? `${cut.slice(0, 70)}…` : cut;
}

/** 지도에 없을 수 있는 출처의 호(관세율표 명칭·관세사 이력) — 지도가 그 호의 소호·국내 세번까지 점선으로 그린다. */
export function sourceHeadings(sources) {
  const out = [];
  for (const r of Array.isArray(sources?.rows) ? sources.rows : []) {
    // 결정례의 호도 넣는다 — 빠지면 출처별 세번의 결정례 줄을 눌러도 지도에 갈 마디가 없다(2026-09-28 금 합금 바 → 8505).
    const hs4 = String(r.code || "").slice(0, 4);
    const why = r.kind === "history" ? "관세사 이력 1위의 호" : r.kind === "precedent" ? "국내 결정례의 호" : r.kind === "model" ? "PROVE 모델 제안의 호" : "관세율표 명칭 일치";
    if (hs4.length === 4 && !out.some((x) => x.hs4 === hs4)) out.push({ hs4, why });
  }
  return out;
}
